import React, { useEffect, useMemo, useState } from 'react';
import { Input, Message, Radio, RadioGroup, SelectPicker } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { describeExpenseGroup } from '../../utils/expenseGroups';

// Files an expense that was entered on its own into a batch, either one that
// already exists or a new one named here. Offered from the expense card rather
// than the edit form: it changes where the expense lives, not what it says.
export default function ExpenseMoveToGroupModal({
  open,
  onClose,
  expense,
  groups = [],
  onMove,
  loading = false,
}) {
  const [mode, setMode] = useState('existing');
  const [groupId, setGroupId] = useState(null);
  const [groupName, setGroupName] = useState('');
  const expenseShown = useLastPresent(expense); // keeps the dialog steady while it fades out

  // Only batches belonging to the same employee can take this expense — the API
  // enforces it, so offering the rest would only produce a rejected move.
  const eligibleGroups = useMemo(
    () => groups.filter((group) => {
      if (!expense?.employee_id) return true;
      return String(group.employeeId || '') === String(expense.employee_id);
    }),
    [groups, expense?.employee_id]
  );

  const groupOptions = useMemo(
    () => eligibleGroups.map((group) => ({
      label: group.name,
      value: group.id,
      description: describeExpenseGroup(group),
    })),
    [eligibleGroups]
  );

  useEffect(() => {
    if (!open) return;
    // Default to creating one when there is nothing to join.
    setMode(eligibleGroups.length > 0 ? 'existing' : 'new');
    setGroupId(null);
    setGroupName('');
  }, [open, eligibleGroups.length]);

  const canMove = mode === 'existing' ? Boolean(groupId) : groupName.trim().length > 0;

  const submit = () => {
    if (!canMove || loading) return;
    onMove?.(mode === 'existing' ? { groupId } : { name: groupName.trim() });
  };

  const selectedGroup = eligibleGroups.find((group) => group.id === groupId) || null;

  return (
    <Modal open={open} onClose={loading ? undefined : onClose} size="xs">
      <Modal.Header>
        <Modal.Title>Move into expense group</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {expenseShown?.description
            ? <>Moving <b>{expenseShown.description}</b>.</>
            : 'Moving this expense.'}
        </div>

        <RadioGroup
          value={mode}
          onChange={setMode}
          name="move-to-group-mode"
        >
          <Radio value="existing" disabled={eligibleGroups.length === 0 || loading}>
            Add to an existing group
          </Radio>
          <Radio value="new" disabled={loading}>Create a new group</Radio>
        </RadioGroup>

        {mode === 'existing' ? (
          <div className="mt-2">
            <SelectPicker
              data={groupOptions}
              value={groupId}
              onChange={setGroupId}
              block
              cleanable={false}
              disabled={loading || eligibleGroups.length === 0}
              placeholder={eligibleGroups.length === 0 ? 'No groups yet' : 'Select a group'}
              renderMenuItem={(label, item) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{label}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>{item.description}</div>
                </div>
              )}
            />
            {selectedGroup?.isSubmitted ? (
              <Message type="warning" showIcon style={{ marginTop: 8 }}>
                This group has already been submitted, so this expense goes to finance for
                review straight away.
              </Message>
            ) : null}
          </div>
        ) : (
          <div className="mt-2">
            <Input
              value={groupName}
              onChange={setGroupName}
              placeholder="e.g. Seattle onsite - January"
              disabled={loading}
              onPressEnter={submit}
            />
            <div className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
              The new group starts with this expense in it. Submit the group when everything
              belonging to it has been added.
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={!canMove || loading} loading={loading}>
          Move
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
